import {
  Drawer,
  Timeline,
  Button,
  Tabs,
  Empty,
  Spin,
  Input,
  Select,
} from "antd";
import {
  ArrowRightLeft,
  User,
  Edit2,
  CheckCircle2,
  ArrowRight,
  Search,
} from "lucide-react";
import { ReactComponent as History } from "../../assets/svg/lead-history.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getLeadHistory } from "../../actionCreator/leadDetailsActionCreator";
import profile from "../../assets/svg/lead-profile.svg";
import { renderLeadStatus } from "../../utils/renderLeadStatus";
import Loader from "../loader";
import { PAGE_LENGTH } from "../../config";

const ACTION_TYPE_CONFIG = {
  TRANSFER: {
    icon: <ArrowRightLeft size={16} className="tw-text-emerald-600" />,
    title: "Lead Transferred",
  },
  STATUS_UPDATE: {
    icon: <CheckCircle2 size={16} className="tw-text-emerald-600" />,
    title: "Status Updated",
  },
  UPDATE: {
    icon: <Edit2 size={16} className="tw-text-emerald-600" />,
    title: "Lead Updated",
  },
  FIELD_UPDATE: {
    icon: <Edit2 size={16} className="tw-text-emerald-600" />,
    title: "Field Updated",
  },
  CUSTOM_FIELD_UPDATE: {
    icon: <Edit2 size={16} className="tw-text-emerald-600" />,
    title: "Custom Field Updated",
  },
  ASSIGN: {
    icon: <User size={16} className="tw-text-emerald-600" />,
    title: "Owner Assigned",
  },
  DEFAULT: {
    icon: <CheckCircle2 size={16} className="tw-text-emerald-600" />,
    title: "Activity Logged",
  },
};

const getLeadCustomFields = (lead) => {
  const wtlFields = Array.isArray(lead?.wtlCustomFields)
    ? lead.wtlCustomFields
    : [];
  const customFields = Array.isArray(lead?.customFields)
    ? lead.customFields
    : [];

  return [...wtlFields, ...customFields]
    .map((f) => ({
      fieldName: f?.fieldName || f?.definition?.fieldName || f?.details,
    }))
    .filter((f) => f.fieldName);
};

const HISTORY_FIELD_FILTER_OPTIONS = [
  { value: "firstname", label: "First Name" },
  { value: "lastname", label: "Last Name" },
  { value: "title", label: "Designation" },
  { value: "phoneNo", label: "Phone" },
  { value: "mobile", label: "Mobile" },
  { value: "emailId", label: "Email" },
  { value: "birthDate", label: "Birth Date" },
  { value: "anniversaryDate", label: "Anniversary Date" },
  { value: "companyName", label: "Company Name" },
  { value: "website", label: "Website" },
  { value: "industryText", label: "Industry" },
  { value: "status", label: "Lead Status" },
  { value: "rate", label: "Potential Revenue" },
  { value: "oppurtunityAmount", label: "Opportunity Amount" },
  { value: "leadSource", label: "Lead Source" },
  { value: "leadRating", label: "Lead Rating" },
  { value: "address", label: "Address" },
  { value: "description", label: "Description" },
];

const getActionConfig = (actionType) =>
  ACTION_TYPE_CONFIG[actionType] || ACTION_TYPE_CONFIG.DEFAULT;

const getUserDisplayName = (user) => {
  if (!user) return "Someone";

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  return fullName || user?.name || user?.email || "Someone";
};

const formatHistoryValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const fullName = [value?.firstName, value?.lastName]
      .filter(Boolean)
      .join(" ");
    return fullName || value?.name || value?.email || JSON.stringify(value);
  }
  return String(value);
};

const formatFieldLabel = (value) => {
  if (!value) return "Field";

  return String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveFieldValue = (fieldName, value, leadStatusListing = []) => {
  const normalizedField = String(fieldName || "")
    .trim()
    .toLowerCase();

  if (normalizedField === "status") {
    const matchedStatus = leadStatusListing?.find(
      (status) =>
        String(status?.id) === String(value) || status?.text === value,
    );

    return matchedStatus?.text || formatHistoryValue(value);
  }

  return formatHistoryValue(value);
};

const renderActionDescription = (item, leadStatusListing = []) => {
  const actionType = item?.actionType;
  const performedByName = getUserDisplayName(item?.performedBy);
  const oldUserName = getUserDisplayName(item?.oldValue);
  const newUserName = getUserDisplayName(item?.newValue);
  const fieldLabel = formatFieldLabel(item?.details);
  const oldValue = resolveFieldValue(
    item?.details,
    item?.oldValue,
    leadStatusListing,
  );
  const newValue = resolveFieldValue(
    item?.details,
    item?.newValue,
    leadStatusListing,
  );

  const performerNode = (
    <span className="tw-font-medium tw-text-text-primary">
      {performedByName}
    </span>
  );

  switch (actionType) {
    case "TRANSFER":
      return (
        <>
          Ownership was transferred from {oldUserName} to {newUserName} by{" "}
          {performerNode}.
        </>
      );
    case "ASSIGN":
      return (
        <>
          Lead ownership was assigned to {newUserName} by {performerNode}.
        </>
      );
    case "STATUS_UPDATE":
      return item?.details ? (
        <>
          {item.details} by {performerNode}.
        </>
      ) : (
        <>Lead status was updated by {performerNode}.</>
      );
    case "CUSTOM_FIELD_UPDATE":
    case "FIELD_UPDATE":
      if (oldValue && newValue) {
        return (
          <>
            {fieldLabel} was changed from "{oldValue}" to "{newValue}" by{" "}
            {performerNode}.
          </>
        );
      }
      if (!oldValue && newValue) {
        return (
          <>
            {fieldLabel} was updated to "{newValue}" by {performerNode}.
          </>
        );
      }
      if (oldValue && !newValue) {
        return (
          <>
            {fieldLabel} value "{oldValue}" was removed by {performerNode}.
          </>
        );
      }
      return (
        <>
          {fieldLabel} was updated by {performerNode}.
        </>
      );
    case "UPDATE":
      return item?.details ? (
        <>
          {item.details} by {performerNode}.
        </>
      ) : (
        <>Lead information was updated by {performerNode}.</>
      );
    default:
      return item?.details ? (
        <>
          {item.details} by {performerNode}.
        </>
      ) : (
        <>Activity was logged by {performerNode}.</>
      );
  }
};

export default function LeadActivityDrawer({
  lead,
  onClose,
  initialFieldFilter,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historySearch, setHistorySearch] = useState("");
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState("");
  const [historyFieldFilter, setHistoryFieldFilter] = useState(undefined);
  const [historyMeta, setHistoryMeta] = useState({
    page: 0,
    totalElements: 0,
    totalPages: 0,
    size: PAGE_LENGTH,
  });
  const historyScrollRef = useRef(null);
  const leadData = lead?.name || lead;
  const isFieldHistoryView = !!initialFieldFilter;
  const isLeadDetailsPage = location.pathname.includes("/lead-details/");

  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const leadId = leadData?.id;
  const historyFieldFilterOptions = useMemo(() => {
    const customFieldOptions = getLeadCustomFields(leadData).map((field) => ({
      value: field?.fieldName,
      label: field?.fieldName,
    }));

    const mergedOptions = [
      ...HISTORY_FIELD_FILTER_OPTIONS,
      ...customFieldOptions,
    ]
      .filter((option) => option?.value)
      .filter(
        (option, index, array) =>
          array.findIndex((item) => item.value === option.value) === index,
      );

    return mergedOptions;
  }, [leadData]);

  useEffect(() => {
    setHistoryFieldFilter(initialFieldFilter);
  }, [initialFieldFilter, leadId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHistorySearch(historySearch.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [historySearch]);

  const loadHistory = async (page, append = false) => {
    if (!leadId) return;

    if (append) {
      setIsFetchingNextPage(true);
    } else {
      setIsHistoryLoading(true);
    }

    try {
      const response = await dispatch(
        getLeadHistory(leadId, {
          page,
          size: PAGE_LENGTH,
          searchTerm: debouncedHistorySearch,
          fieldFilter: historyFieldFilter,
        }),
      );

      const nextItems = response?.content || [];

      setHistoryItems((prev) => {
        if (!append) return nextItems;

        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNextItems = nextItems.filter(
          (item) => !existingIds.has(item.id),
        );
        return [...prev, ...uniqueNextItems];
      });

      setHistoryMeta({
        page: response?.number ?? page,
        totalElements: response?.totalElements ?? nextItems.length,
        totalPages: response?.totalPages ?? 0,
        size: response?.size ?? PAGE_LENGTH,
      });
    } finally {
      if (append) {
        setIsFetchingNextPage(false);
      } else {
        setIsHistoryLoading(false);
      }
    }
  };

  useEffect(() => {
    if (leadId) {
      setHistoryItems([]);
      setHistoryMeta({
        page: 0,
        totalElements: 0,
        totalPages: 0,
        size: PAGE_LENGTH,
      });
      loadHistory(0);
    } else {
      setHistorySearch("");
      setDebouncedHistorySearch("");
      setHistoryFieldFilter(undefined);
      setHistoryItems([]);
      setHistoryMeta({
        page: 0,
        totalElements: 0,
        totalPages: 0,
        size: PAGE_LENGTH,
      });
      setIsHistoryLoading(false);
    }
  }, [leadId, debouncedHistorySearch, historyFieldFilter]);

  const getHistoryTimelineDot = (actionType) => {
    const configMap = {
      TRANSFER: {
        icon: <ArrowRightLeft size={14} className="tw-text-blue-500" />,
        bg: "tw-bg-blue-50/70",
        border: "tw-border-blue-100",
      },
      STATUS_UPDATE: {
        icon: <CheckCircle2 size={14} className="tw-text-emerald-500" />,
        bg: "tw-bg-emerald-50/70",
        border: "tw-border-emerald-100",
      },
      UPDATE: {
        icon: <Edit2 size={14} className="tw-text-indigo-500" />,
        bg: "tw-bg-indigo-50/70",
        border: "tw-border-indigo-100",
      },
      FIELD_UPDATE: {
        icon: <Edit2 size={14} className="tw-text-indigo-500" />,
        bg: "tw-bg-indigo-50/70",
        border: "tw-border-indigo-100",
      },
      CUSTOM_FIELD_UPDATE: {
        icon: <Edit2 size={14} className="tw-text-indigo-500" />,
        bg: "tw-bg-indigo-50/70",
        border: "tw-border-indigo-100",
      },
      ASSIGN: {
        icon: <User size={14} className="tw-text-purple-500" />,
        bg: "tw-bg-purple-50/70",
        border: "tw-border-purple-100",
      },
      DEFAULT: {
        icon: <CheckCircle2 size={14} className="tw-text-slate-500" />,
        bg: "tw-bg-slate-50/70",
        border: "tw-border-slate-100",
      },
    };

    const config = configMap[actionType] || configMap.DEFAULT;

    return (
      <div
        className={`tw-size-7 tw-rounded-full tw-border tw-border-solid ${config.border} ${config.bg} tw-flex tw-items-center tw-justify-center tw-shadow-sm`}
      >
        {config.icon}
      </div>
    );
  };

  const timelineItems = historyItems?.map((item) => {
    return {
      key: item.id,
      dot: getHistoryTimelineDot(item.actionType),
      children: (
        <div className="tw-ml-2 tw-pb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
            <span className="tw-font-bold tw-text-sm tw-text-slate-800">
              {getActionConfig(item.actionType).title}
            </span>
            <span className="tw-text-[11px] tw-font-semibold tw-text-slate-400">
              {moment(item.createdDate).format("MMM D, YYYY [at] h:mm A")}
            </span>
          </div>

          <p className="tw-text-xs tw-text-slate-500 tw-leading-relaxed tw-font-medium tw-m-0">
            {renderActionDescription(item, leadStatusListing)}
          </p>
        </div>
      ),
    };
  });

  const handleHistoryScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
    const hasMorePages = historyMeta.page + 1 < historyMeta.totalPages;

    if (
      isNearBottom &&
      hasMorePages &&
      !isHistoryLoading &&
      !isFetchingNextPage
    ) {
      loadHistory(historyMeta.page + 1, true);
    }
  };

  useEffect(() => {
    const scrollContainer = historyScrollRef.current;
    const hasMorePages = historyMeta.page + 1 < historyMeta.totalPages;

    if (
      scrollContainer &&
      timelineItems?.length &&
      hasMorePages &&
      !isHistoryLoading &&
      !isFetchingNextPage &&
      scrollContainer.scrollHeight <= scrollContainer.clientHeight
    ) {
      loadHistory(historyMeta.page + 1, true);
    }
  }, [
    timelineItems?.length,
    historyMeta.page,
    historyMeta.totalPages,
    isHistoryLoading,
    isFetchingNextPage,
  ]);

  return (
    <Drawer
      title={
        <div>
          <div className="tw-flex tw-gap-3 tw-items-start">
            <img src={profile} alt="" />
            <div className="tw-flex-1">
              <div className="tw-flex tw-items-center tw-gap-2 tw-text-text-primary">
                <h3 className="tw-mb-0 tw-text-lg tw-font-semibold">
                  {leadData?.firstname} {leadData?.lastname}
                </h3>

                {renderLeadStatus(
                  leadStatusListing?.find((i) => i?.id === leadData?.status)
                    ?.text,
                )}
              </div>
              {leadData?.title && (
                <p className="tw-text-sm tw-mb-0">{leadData?.title}</p>
              )}
            </div>
          </div>

          <p className="tw-text-xs tw-font-normal tw-text-sub-text tw-mt-3">
            Current Owner :{" "}
            <span className="tw-text-text-primary">
              {leadData?.ownerName || "-"}
            </span>
          </p>
        </div>
      }
      placement="right"
      width={420}
      onClose={onClose}
      open={!!lead}
      closable={false}
      bodyStyle={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Tabs
        className="tw-flex-1 tw-min-h-0 tw-overflow-hidden custom-tab lead-activity-tabs"
        items={[
          {
            key: "1",
            label: (
              <div className="tw-flex tw-gap-2 tw-items-center">
                <History />
                History
              </div>
            ),
            children: isHistoryLoading ? (
              <div className="tw-h-full tw-overflow-y-auto tw-py-10">
                <Loader />
              </div>
            ) : timelineItems?.length ? (
              <div
                ref={historyScrollRef}
                className="tw-h-full tw-overflow-y-auto"
                onScroll={handleHistoryScroll}
              >
                <div className="tw-px-5 tw-pt-5 tw-sticky tw-top-0 tw-z-[1] tw-bg-white">
                  <div className="tw-flex tw-gap-3 tw-mb-3">
                    {!isFieldHistoryView && (
                      <Select
                        allowClear
                        value={historyFieldFilter}
                        onChange={setHistoryFieldFilter}
                        placeholder="All fields"
                        options={historyFieldFilterOptions}
                        className="tw-w-[180px]"
                      />
                    )}
                    <Input
                      allowClear
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Search history"
                      prefix={<Search size={16} className="tw-text-slate-400" />}
                    />
                  </div>
                </div>
                <Timeline className="tw-mt-7 tw-mx-7" items={timelineItems} />
                {isFetchingNextPage && (
                  <div className="tw-px-5 tw-pb-5 tw-flex tw-justify-center">
                    <Spin size="small" />
                  </div>
                )}
              </div>
            ) : (
              <div className="tw-h-full tw-overflow-y-auto">
                <div className="tw-px-5 tw-pt-5 tw-sticky tw-top-0 tw-z-[1] tw-bg-white">
                  <div className="tw-flex tw-gap-3 tw-mb-3">
                    {!isFieldHistoryView && (
                      <Select
                        allowClear
                        value={historyFieldFilter}
                        onChange={setHistoryFieldFilter}
                        placeholder="All fields"
                        options={historyFieldFilterOptions}
                        className="tw-w-[180px]"
                      />
                    )}
                    <Input
                      allowClear
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Search history"
                      prefix={<Search size={16} className="tw-text-slate-400" />}
                    />
                  </div>
                </div>
                <div className="tw-flex tw-items-center tw-justify-center tw-h-[calc(100%-88px)]">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No history found"
                  />
                </div>
              </div>
            ),
          },
        ]}
      />

      {!isLeadDetailsPage && (
        <div className="tw-shrink-0 tw-bg-secondary-color tw-border-t tw-p-3 tw-flex tw-gap-2">
          <Button
            type="primary"
            className="tw-flex-1 !tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
            onClick={() => navigate(`/lead-details/${leadId}`)}
          >
            View Full Lead Details <ArrowRight size={16} className="tw-ml-1.5 tw-inline" />
          </Button>
        </div>
      )}
    </Drawer>
  );
}
